// The ask/confirm split for a consequential act on one row — the generic half of
// `createMachineRemove`. Suspending a user, revoking an invitation, declining an
// access request and the operator's `platform` overrides each change something
// a single stray click must not (their MCP twins require `confirm: true`), and
// each fired on one click (#5608 slice 17 rows 1 and 16). Here a click only
// ASKS; the act leaves on `confirm()`, for the row that was asked about.
//
// The act's own busy key and failure notice stay with the act (a
// `createKeyedMutation` or `createMutation` the caller already holds): this
// owns only WHICH row awaits confirmation. Feed `pending()` to a
// `<ConfirmDialog/>`.

import { createSignal } from 'solid-js';

export type ConfirmGate<T> = {
  /** The row awaiting confirmation, or null. */
  readonly pending: () => T | null;
  /** Ask about a row. Sends nothing. */
  readonly ask: (row: T) => void;
  /** Dismiss without acting. */
  readonly cancel: () => void;
  /**
   * Act on the asked row. Nothing asked, or a confirm already in flight: a
   * no-op. Settling closes the confirm only if it still shows THIS row — the
   * reader may have dismissed it and asked about another meanwhile.
   */
  readonly confirm: () => Promise<void>;
};

export function createConfirmGate<T>(input: {
  /** The act. Its failure is the act's to report; a rejection here is a bug. */
  readonly act: (row: T) => Promise<void>;
}): ConfirmGate<T> {
  const [pending, setPending] = createSignal<T | null>(null);
  let inFlight = false;

  const confirm = async (): Promise<void> => {
    const row = pending();
    if (row === null || inFlight) return;
    inFlight = true;
    try {
      await input.act(row);
    } finally {
      inFlight = false;
      if (pending() === row) setPending(null);
    }
  };

  return {
    pending,
    // The setter's function form would CALL a row that is itself a function.
    ask: (row) => setPending(() => row),
    cancel: () => setPending(null),
    confirm,
  };
}
